import React from 'react';
import {
  FaCalendarCheck,
  FaCalendarAlt,
  FaClock,
  FaUmbrellaBeach,
  FaReceipt,
  FaMoneyBillWave,
  FaTaxi,
  FaFileInvoice,
} from 'react-icons/fa';
import { PersonalAttendance, PersonalLeaves, PersonalClaims } from '../../../types/dashboard';
import { SectionCard, StatusBadge, MiniStat, ProgressBar, DataTable, formatCurrency, formatDate } from './ui';
import { getAttendanceLeaveTypeLabel } from '../../../libs/attendance';
import { DoughnutChart, ProgressRing, CHART_COLORS } from './charts';

// Personal Attendance Section
interface AttendanceSectionProps {
  data: PersonalAttendance;
}

export const AttendanceSection: React.FC<AttendanceSectionProps> = ({ data }) => {
  const { today, this_month, this_year } = data;

  return (
    <SectionCard title="Attendance" subtitle="Today & this month" icon={FaCalendarCheck}>
      <div className="space-y-6">
        {/* Today's Status */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-blue-600 font-medium uppercase tracking-wide">Today</p>
              <p className="text-sm text-gray-600 mt-0.5">{formatDate(today.date, { weekday: 'long' })}</p>
            </div>
            <StatusBadge status={today.status} size="md" />
          </div>
          {today.work_status && today.work_status !== 'pending' && (
            <div className="mt-3 flex items-center gap-4 text-sm">
              {today.check_in_time && (
                <div className="flex items-center gap-1">
                  <FaClock className="w-3 h-3 text-green-500" />
                  <span className="text-gray-600">In: <span className="font-medium">{today.check_in_time}</span></span>
                </div>
              )}
              {today.check_out_time && (
                <div className="flex items-center gap-1">
                  <FaClock className="w-3 h-3 text-red-500" />
                  <span className="text-gray-600">Out: <span className="font-medium">{today.check_out_time}</span></span>
                </div>
              )}
              {today.worked_hours !== undefined && (
                <div className="flex items-center gap-1">
                  <span className="text-gray-600">Hours: <span className="font-medium">{today?.worked_hours?today?.worked_hours : 0}h</span></span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Monthly Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <MiniStat label="Present" value={this_month.present_days} color="green" />
          <MiniStat label="Absent" value={this_month.absent_days} color="red" />
          <MiniStat label="Leaves" value={this_month.leave_days} color="yellow" />
          <MiniStat label="Weekly Off" value={this_month.weekly_off} color="blue" />
        </div>

        {/* Attendance Chart & Progress */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="h-48">
            <DoughnutChart
              labels={['Present', 'Absent', 'Leave', 'Weekly Off', 'Holiday']}
              data={[
                this_month.present_days,
                this_month.absent_days,
                this_month.leave_days,
                this_month.weekly_off,
                this_month.holidays,
              ]}
              colors={['#22c55e', '#ef4444', '#eab308', '#3b82f6', '#8b5cf6']}
              centerText={`${Math.round(this_month.attendance_percentage)}%`}
              centerSubtext="Attendance"
            />
          </div>
          <div className="space-y-4">
            <div>
              <p className="text-xs text-gray-500 mb-1">Total Worked Hours</p>
              <p className="text-2xl font-bold text-gray-900">{this_month?.total_worked_hours? this_month?.total_worked_hours : 0}h</p>
            </div>
            <ProgressBar
              value={this_month.attendance_percentage}
              label="Monthly Attendance Rate"
              color={this_month.attendance_percentage >= 80 ? 'green' : this_month.attendance_percentage >= 60 ? 'yellow' : 'red'}
            />
            <div className="text-xs text-gray-500">
              <p>This Year: {this_year.present_days} present / {this_year.total_recorded} total</p>
            </div>
          </div>
        </div>
      </div>
    </SectionCard>
  );
};

// Personal Leaves Section
interface LeavesSectionProps {
  data: PersonalLeaves;
}

export const LeavesSection: React.FC<LeavesSectionProps> = ({ data }) => {
  const { balance, applications, by_type, recent_applications, pending_applications } = data;

  return (
    <SectionCard title="Leaves" subtitle="Balance & applications" icon={FaUmbrellaBeach}>
      <div className="space-y-6">
        {/* Leave Balance */}
        <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg p-4 border border-purple-100">
          <p className="text-xs text-purple-600 font-medium uppercase tracking-wide mb-3">Casual Leave Balance</p>
          <div className="flex items-center gap-6">
            <ProgressRing
              value={balance.casual_leave_balance}
              maxValue={balance.casual_leave_total}
              label="Available"
              size="md"
              color="#8b5cf6"
            />
            <div className="space-y-1">
              <p className="text-sm text-gray-600">Total: <span className="font-medium">{balance.casual_leave_total}</span></p>
              <p className="text-sm text-gray-600">Used: <span className="font-medium">{balance.casual_leave_used}</span></p>
              <p className="text-sm text-gray-600">Balance: <span className="font-bold text-purple-700">{balance.casual_leave_balance}</span></p>
            </div>
          </div>
        </div>

        {/* Other Leaves Used */}
        <div className="grid grid-cols-3 gap-3">
          <MiniStat label="Medical" value={balance.medical_leave_used} color="red" />
          <MiniStat label="Accidental" value={balance.accidental_leave_used} color="yellow" />
          <MiniStat label="Special Approval" value={balance.other_leave_used} color="gray" />
        </div>

        {/* Applications Summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="text-center p-3 bg-gray-50 rounded-lg">
            <p className="text-2xl font-bold text-gray-900">{applications.total}</p>
            <p className="text-xs text-gray-500">Total</p>
          </div>
          <div className="text-center p-3 bg-yellow-50 rounded-lg">
            <p className="text-2xl font-bold text-yellow-700">{applications.pending}</p>
            <p className="text-xs text-yellow-600">Pending</p>
          </div>
          <div className="text-center p-3 bg-green-50 rounded-lg">
            <p className="text-2xl font-bold text-green-700">{applications.approved}</p>
            <p className="text-xs text-green-600">Approved</p>
          </div>
          <div className="text-center p-3 bg-red-50 rounded-lg">
            <p className="text-2xl font-bold text-red-700">{applications.rejected}</p>
            <p className="text-xs text-red-600">Rejected</p>
          </div>
        </div>

        {/* Pending Applications */}
        {pending_applications.length > 0 && (
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2">Pending Applications</h4>
            <DataTable
              columns={[
                { key: 'leave_type', label: 'Type', render: (item) => getAttendanceLeaveTypeLabel(item.leave_type) },
                { key: 'start_date', label: 'From', render: (item) => formatDate(item.start_date) },
                { key: 'end_date', label: 'To', render: (item) => formatDate(item.end_date) },
                { key: 'total_days', label: 'Days', align: 'center' },
              ]}
              data={pending_applications}
              maxHeight="200px"
            />
          </div>
        )}

        {/* Recent Applications */}
        {recent_applications.length > 0 && (
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2">Recent Applications</h4>
            <DataTable
              columns={[
                { key: 'leave_type', label: 'Type', render: (item) => getAttendanceLeaveTypeLabel(item.leave_type) },
                { key: 'start_date', label: 'From', render: (item) => formatDate(item.start_date) },
                { key: 'total_days', label: 'Days', align: 'center' },
                { key: 'status', label: 'Status', render: (item) => <StatusBadge status={item.status} /> },
              ]}
              data={recent_applications.slice(0, 5)}
              maxHeight="200px"
            />
          </div>
        )}
      </div>
    </SectionCard>
  );
};

// Personal Claims Section
interface ClaimsSectionProps {
  data: PersonalClaims;
}

export const ClaimsSection: React.FC<ClaimsSectionProps> = ({ data }) => {
  const { project_claims, general_claims, cab_hiring, advances } = data;

  const claimCategories = [
    {
      title: 'Project Claims',
      icon: FaReceipt,
      total: project_claims.total,
      pending: project_claims.pending,
      approvedCount: project_claims.approved,
      submitted: project_claims.total_submitted,
      approved: project_claims.total_approved,
      color: 'blue',
    },
    {
      title: 'General Claims',
      icon: FaFileInvoice,
      total: general_claims.total,
      pending: general_claims.pending,
      approvedCount: general_claims.approved,
      submitted: general_claims.total_submitted,
      approved: general_claims.total_approved,
      color: 'green',
    },
    {
      title: 'Cab Hiring',
      icon: FaTaxi,
      total: cab_hiring.total,
      pending: cab_hiring.in_progress,
      approvedCount: cab_hiring.approved,
      submitted: cab_hiring.total_amount,
      approved: cab_hiring.total_amount,
      color: 'yellow',
    },
    {
      title: 'Advances',
      icon: FaMoneyBillWave,
      total: advances.total,
      pending: advances.pending,
      approvedCount: advances.approved,
      submitted: advances.total_requested,
      approved: advances.total_approved,
      color: 'purple',
    },
  ];

  return (
    <SectionCard title="Project Claims & Advances" subtitle="Financial requests overview" icon={FaReceipt}>
      <div className="space-y-6">
        {/* Claims Overview Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {claimCategories.map((category) => {
            const colorClasses: Record<string, { bg: string; border: string; text: string }> = {
              blue: { bg: 'from-blue-50 to-indigo-50', border: 'border-blue-100', text: 'text-blue-700' },
              green: { bg: 'from-green-50 to-emerald-50', border: 'border-green-100', text: 'text-green-700' },
              yellow: { bg: 'from-yellow-50 to-amber-50', border: 'border-yellow-100', text: 'text-yellow-700' },
              purple: { bg: 'from-purple-50 to-violet-50', border: 'border-purple-100', text: 'text-purple-700' },
            };
            const colors = colorClasses[category.color];
            const Icon = category.icon;

            return (
              <div
                key={category.title}
                className={`bg-gradient-to-r ${colors.bg} rounded-lg p-4 border ${colors.border}`}
              >
                <div className="flex items-center gap-2 mb-3">
                  <Icon className={`w-4 h-4 ${colors.text}`} />
                  <p className={`text-xs font-medium ${colors.text} uppercase tracking-wide`}>
                    {category.title}
                  </p>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Total</span>
                    <span className="font-medium">{category.total}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Pending</span>
                    <span className="font-medium text-yellow-600">{category.pending}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Approved</span>
                    <span className="font-medium text-green-600">{category.approvedCount}</span>
                  </div>
                  {category.submitted > 0 && (
                    <div className="pt-2 border-t border-gray-200">
                      <p className="text-xs text-gray-500">
                        Approved: <span className="font-medium">{formatCurrency(category.approved)}</span>
                      </p>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Claims Chart */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="h-48">
            <DoughnutChart
              labels={['Project Claims', 'General Claims', 'Cab Hiring', 'Advances']}
              data={[
                project_claims.total,
                general_claims.total,
                cab_hiring.total,
                advances.total,
              ]}
              colors={[CHART_COLORS.primary[0], CHART_COLORS.success[0], CHART_COLORS.warning[0], '#8b5cf6']}
              title="Claims Distribution"
            />
          </div>
          <div className="space-y-4">
            <div>
              <p className="text-xs text-gray-500 mb-1">Total Approved Amount</p>
              <p className="text-2xl font-bold text-green-700">
                {formatCurrency(
                  project_claims.total_approved +
                  general_claims.total_approved +
                  advances.total_approved +
                  cab_hiring.total_amount
                )}
              </p>
            </div>
            <div className="space-y-2">
              <ProgressBar
                value={project_claims.approved}
                maxValue={project_claims.total || 1}
                label="Project Claims Approval"
                color="blue"
              />
              <ProgressBar
                value={general_claims.approved}
                maxValue={general_claims.total || 1}
                label="General Claims Approval"
                color="green"
              />
              <ProgressBar
                value={advances.approved}
                maxValue={advances.total || 1}
                label="Advances Approval"
                color="yellow"
              />
            </div>
          </div>
        </div>
      </div>
    </SectionCard>
  );
};

// Combined Personal Dashboard Section
interface PersonalDashboardProps {
  attendance?: PersonalAttendance;
  leaves?: PersonalLeaves;
  claims?: PersonalClaims;
}

export const PersonalDashboard: React.FC<PersonalDashboardProps> = ({
  attendance,
  leaves,
  claims,
}) => {
  return (
    <div className="space-y-6">
      <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
        <FaCalendarAlt className="w-5 h-5 text-blue-600" />
        Personal Overview
      </h2>
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {attendance && <AttendanceSection data={attendance} />}
        {leaves && <LeavesSection data={leaves} />}
        {claims && <ClaimsSection data={claims} />}
      </div>
    </div>
  );
};
